import {
  CalendarRange,
  ClipboardList,
  CircleUserRound,
  Handshake,
  Heart,
  IdCard,
  KeyRound,
  BookOpen,
  MessageSquareText,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Users,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { DiveMask } from "@/components/icons/DiveMask";

/**
 * App Control Panel — internal navigation surface for authenticated
 * users.  Strictly separates produktive routes from disabled (planned)
 * items: disabled entries do NOT carry a route and never render as a
 * link, preventing fake-CTA regressions.
 */
export type ControlPanelItem = {
  key: string;
  labelKey: string;
  icon: LucideIcon;
  /** When omitted the row is rendered as disabled (`bald`). */
  to?: string;
  /** Visual hint for non-stable surfaces ("Beta"). */
  beta?: boolean;
};

export type ControlPanelSectionConfig = {
  key: string;
  titleKey: string;
  items: ControlPanelItem[];
  /** Optional permission gate applied at render time. */
  permission?: "spotStudio" | "admin";
};

export const CONTROL_PANEL_HEADER_ACTIONS = {
  profile: { to: "/profile", labelKey: "controlPanel.header.profile", icon: CircleUserRound },
  communication: {
    // Disabled placeholder — communication hub is not productive yet.
    to: undefined as string | undefined,
    labelKey: "controlPanel.header.communication",
    soonKey: "controlPanel.header.communicationSoon",
    icon: MessageSquareText,
  },
  settings: { to: "/profile?tab=settings", labelKey: "controlPanel.header.settings", icon: Settings },
} as const;

export const CONTROL_PANEL_SECTIONS: readonly ControlPanelSectionConfig[] = [
  // v3.3 — Active, productive sections come first. Disabled / future
  // items live exclusively in the slim "Demnächst" section at the
  // bottom so the panel never opens on a wall of "bald" rows.
  {
    key: "account",
    titleKey: "controlPanel.sections.account",
    items: [
      { key: "profile", labelKey: "controlPanel.items.profile", icon: IdCard, to: "/profile?tab=personal" },
      { key: "diverProfile", labelKey: "controlPanel.items.diverProfile", icon: DiveMask as unknown as LucideIcon, to: "/profile?tab=diver" },
      { key: "favorites", labelKey: "controlPanel.items.favorites", icon: Heart, to: "/profile?tab=favorites" },
      { key: "settings", labelKey: "controlPanel.items.settings", icon: SlidersHorizontal, to: "/profile?tab=settings" },
    ],
  },
  {
    // "Mein Tauchen" — produktive Tauch-Module (aktuell nur das
    // Logbuch-/Dive-Activity-Modul). Beta-Status wird hier bewusst
    // NICHT als Badge gezeigt: der Modul-Header trägt den Hinweis
    // selbst, die Sidepanel-Navigation bleibt ruhig.
    // Hinweis: Sektion sitzt bewusst NACH `account`, weil eine IA-
    // Regression-Test (ProfileNavigation.ia) `account` als erste
    // produktive Sektion festschreibt.
    key: "myDiving",
    titleKey: "controlPanel.sections.myDiving",
    items: [
      {
        key: "myDives",
        labelKey: "controlPanel.items.myDives",
        icon: BookOpen,
        // Base path — active-state matching deckt damit automatisch
        // /logbook, /imports, /imports/:id/review und /logs/:id ab.
        // Der Base-Pfad redirected im Router auf /logbook.
        to: "/my-diving/dive-activity",
      },
    ],
  },
  {
    key: "community",
    titleKey: "controlPanel.sections.community",
    items: [
      { key: "buddynet", labelKey: "controlPanel.items.buddynet", icon: UsersRound, to: "/community/buddynet" },
      { key: "buddymatch", labelKey: "controlPanel.items.buddymatch", icon: Handshake, to: "/community/buddymatch", beta: true },
    ],
  },
  {
    key: "spotStudio",
    titleKey: "controlPanel.sections.spotStudio",
    permission: "spotStudio",
    items: [
      { key: "spotStudio", labelKey: "controlPanel.items.spotStudio", icon: Sparkles, to: "/spot-studio" },
    ],
  },
  {
    key: "admin",
    titleKey: "controlPanel.sections.admin",
    permission: "admin",
    items: [
      { key: "adminDashboard", labelKey: "controlPanel.items.adminDashboard", icon: ShieldCheck, to: "/admin" },
    ],
  },
  // "Demnächst" — disabled / future items, intentionally last so the
  // user never lands on a screen full of "bald" rows.
  {
    key: "comingSoon",
    titleKey: "controlPanel.sections.comingSoon",
    items: [
      { key: "planner", labelKey: "controlPanel.items.planner", icon: ClipboardList },
      { key: "calendar", labelKey: "controlPanel.items.calendar", icon: CalendarRange },
      { key: "groupsChannels", labelKey: "controlPanel.items.groupsChannels", icon: Users },
      { key: "groupPlanning", labelKey: "controlPanel.items.groupPlanning", icon: CalendarRange },
    ],
  },
  // Konto & Sicherheit lebt am Ende, direkt vor dem Logout-Footer —
  // klar als "Konto"-Schluss, nicht als App-Bereich.
  {
    key: "accountSecurity",
    titleKey: "controlPanel.sections.accountSecurity",
    items: [
      { key: "accountSecurity", labelKey: "controlPanel.items.accountSecurity", icon: KeyRound, to: "/profile?tab=account" },
    ],
  },
];

/**
 * Routes on which the control panel must be PERSISTENT (desktop ≥ lg).
 * Public work surfaces (`/guide`, `/visibility`, `/map`, `/providers`,
 * spot/poi detail, home) are intentionally excluded so they retain
 * full-width canvas — the panel stays available there as a drawer
 * via the avatar trigger only.
 */
const INTERNAL_ROUTE_PREFIXES = [
  "/my-diving",
  "/community",
  "/profile",
  "/notifications",
  "/spot-studio",
  "/memberships",
  "/groups",
];

export function isInternalAppRoute(pathname: string): boolean {
  return INTERNAL_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}?`),
  );
}